"use client";

import { useMemo, useState } from "react";
import { BottomNav } from "../_components/bottom-nav";
import { ExploreFooter } from "../_components/explore-footer";
import { TagCard } from "../_components/tag-card";
import { TrendingTagsSection } from "../_components/trending-tags-section";
import type { AllTagsPageData } from "../types";

export function AllTagsPage({ data }: { data: AllTagsPageData }) {
  const { toolTags, topicTags, trendingTags } = data;

  const [searchQuery, setSearchQuery] = useState("");

  const filteredToolTags = useMemo(() => {
    if (!searchQuery) {
      return toolTags;
    }
    const q = searchQuery.toLowerCase();
    return toolTags.filter((t) => t.displayName.toLowerCase().includes(q));
  }, [toolTags, searchQuery]);

  const filteredTopicTags = useMemo(() => {
    if (!searchQuery) {
      return topicTags;
    }
    const q = searchQuery.toLowerCase();
    return topicTags.filter((t) => t.displayName.toLowerCase().includes(q));
  }, [topicTags, searchQuery]);

  return (
    <main
      className="pb-[calc(var(--explore-bottom-nav-h)+var(--explore-safe-bottom)+16px)] lg:pb-0"
      id="main-content"
      tabIndex={-1}
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6">
        {/* Hero */}
        <div className="py-6 pb-4">
          <h1
            className="mb-1.5 font-bold text-[var(--explore-text-primary)]"
            style={{
              fontFamily: "var(--explore-card-title-stack)",
              fontSize: "32px",
              lineHeight: "1.2",
              fontWeight: 700,
            }}
          >
            Browse All Tags
          </h1>
          <p className="max-w-[600px] text-[1rem] text-[var(--explore-text-secondary)]">
            Explore content by topic. Find stories organized by products,
            models, and topics.
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-8 pb-6">
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-[1.5rem] text-[var(--explore-text-primary)]">
              {filteredToolTags.length + filteredTopicTags.length}
            </span>
            <span className="text-[0.9rem] text-[var(--explore-text-muted)]">
              tags
            </span>
          </div>
        </div>

        {/* Trending Tags */}
        {trendingTags.length > 0 && (
          <div className="pb-8">
            <TrendingTagsSection tags={trendingTags} />
          </div>
        )}

        {/* Search */}
        <div className="pb-8">
          <div className="relative max-w-[400px]">
            <svg
              className="-translate-y-1/2 absolute top-1/2 left-3.5 h-4 w-4 text-[var(--explore-text-muted)]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className="w-full rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] py-2.5 pr-4 pl-10 text-[0.85rem] text-[var(--explore-text-secondary)] outline-none transition-all focus:border-[var(--explore-accent-teal)] focus:ring-[3px] focus:ring-[var(--explore-focus-ring)]"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tags..."
              type="text"
              value={searchQuery}
            />
          </div>
        </div>

        {/* Tag Sections */}
        <div className="pb-12">
          {/* Products & Models */}
          {filteredToolTags.length > 0 && (
            <section className="mb-10">
              <div className="mb-4 flex items-center gap-2.5 border-[var(--explore-border-light)] border-b pb-2.5">
                <span className="text-[1.25rem]">🔧</span>
                <h3 className="font-bold text-[1.15rem] text-[var(--explore-text-primary)]">
                  Products & Models
                </h3>
                <span className="ml-auto text-[0.8rem] text-[var(--explore-text-muted)]">
                  {filteredToolTags.length} tags
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredToolTags.map((tag) => (
                  <TagCard key={tag.slug} tag={tag} />
                ))}
              </div>
            </section>
          )}

          {/* Topics */}
          {filteredTopicTags.length > 0 && (
            <section className="mb-10">
              <div className="mb-4 flex items-center gap-2.5 border-[var(--explore-border-light)] border-b pb-2.5">
                <span className="text-[1.25rem]">💡</span>
                <h3 className="font-bold text-[1.15rem] text-[var(--explore-text-primary)]">
                  Topics
                </h3>
                <span className="ml-auto text-[0.8rem] text-[var(--explore-text-muted)]">
                  {filteredTopicTags.length} tags
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredTopicTags.map((tag) => (
                  <TagCard key={tag.slug} tag={tag} />
                ))}
              </div>
            </section>
          )}

          {filteredToolTags.length === 0 && filteredTopicTags.length === 0 && (
            <div className="py-12 text-center text-[var(--explore-text-muted)]">
              No tags match your search.
            </div>
          )}
        </div>
      </div>

      <ExploreFooter />
      <BottomNav />
    </main>
  );
}
